import { FilterTypes } from '@aboutyou/backbone/endpoints/filters/filters'
import {
  MainFilterSelectionEvent,
  RESET_FILTER_SELECTION,
} from '@aboutyou/legacy-tracking-sdk'
import { doAsync } from 'helpers/doAsync'
import { useTracking } from 'helpers/tracking/hooks/useTrackingContextAndClientV6'
import { useCallback } from 'react'
import { PreferredFilterType } from 'services/providers/bapi/entities/filters/types'
import FilterViewStore from 'store/sidecar/FilterViewStore'
import { useFilterStore } from 'store/sidecar/ProductStreamContext'

/**
 * provides a tracking callback that should be called after the
 * filterStore is updated
 */
export const useMainFilterTracking = () => {
  const { TrackingClient } = useTracking()
  const filterStore = useFilterStore()

  return useCallback(
    (event: string | typeof RESET_FILTER_SELECTION) => {
      doAsync(() => {
        if (event === RESET_FILTER_SELECTION) {
          TrackingClient?.trackMainFilterSelection({
            selection: RESET_FILTER_SELECTION,
            action: 'submit',
            label: 'quick_filters',
          })
          return
        }

        const selection = getCurrentSelectionForSlug(filterStore, event)
        if (selection) {
          TrackingClient?.trackMainFilterSelection({
            selection,
            action: 'submit',
            label: 'quick_filters',
          })
        }
      })
    },
    [filterStore, TrackingClient],
  )
}

export function getCurrentSelectionForSlug(
  filterStore: FilterViewStore,
  filterName: string,
): MainFilterSelectionEvent | undefined {
  if (filterName === 'prices') {
    const selectedMinValues = filterStore.selectedFilters['minPrice']
    const selectedMaxValues = filterStore.selectedFilters['maxPrice']
    const availablePriceFilter = filterStore.availablePriceFilter

    if (!selectedMinValues || !selectedMaxValues || !availablePriceFilter) {
      return
    }

    const { min, max } = availablePriceFilter.values[0]
    const selectedMin = Number(selectedMinValues[0])
    const selectedMax = Number(selectedMaxValues[0])

    return {
      slug: filterName,
      selectedValue: [
        isNaN(selectedMin) ? min : selectedMin,
        isNaN(selectedMax) ? max : selectedMax,
      ],
      filterType: 'range',
    }
  }

  const filter = filterStore.allowedFilters.find(({ slug }) => {
    return slug === filterName
  })

  const selectedValues =
    filterStore.selectedFilters[filterName as PreferredFilterType]

  if (!filter || !selectedValues) {
    return undefined
  }

  switch (filter.type) {
    case FilterTypes.BOOLEAN: {
      return {
        slug: filterName,
        selectedValue: selectedValues[0] === 'true',
        filterType: 'boolean',
      }
    }
    case FilterTypes.ATTRIBUTES: {
      if (filterName === 'brand') {
        return {
          slug: filterName,
          selectedValue: selectedValues.map(stripBrandId),
          filterType: 'attribute',
        }
      }
      return {
        slug: filterName,
        selectedValue: selectedValues.map(Number),
        filterType: 'attribute',
      }
    }
    case FilterTypes.RANGE: {
      return {
        slug: filterName,
        selectedValue: [Number(selectedValues[0]), Number(selectedValues[1])],
        filterType: 'range',
      }
    }
  }
}

const stripBrandId = (brandId: string) => {
  const splitId = brandId.split('-')
  const stripedBrandId = splitId[splitId.length - 1]
  return Number(stripedBrandId)
}
